import {
  EnvironmentInjector,
  Injectable,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import {
  Messaging,
  getToken,
  onMessage,
  getMessaging,
} from '@angular/fire/messaging';
import { Apollo, gql } from 'apollo-angular';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { Observable, from, map, switchMap, tap } from 'rxjs';
import { SwPush, SwUpdate } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { UserState } from '../store/user/user.reducer';
import { LogEventsEnum } from '../firebase.model';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { selectUser } from '../store/user/user.selectors';

const UpdateFCM = gql`
  mutation RegisterFCM($token: String!) {
    registerFCM(fcm_token: $token) {
      _id
      createdAt
      updatedAt
    }
  }
`;

const RemoveFCM = gql`
  mutation RemoveFCMTokenMutation($token: String!) {
    removeFCM(fcm_token: $token) {
      _id
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class FCMService {
  private apollo = inject(Apollo);
  private userService = inject(UserService);
  private swPush = inject(SwPush);
  private swUpdate = inject(SwUpdate);
  private analytics = inject(Analytics);
  private store = inject(Store<{ user: UserState }>);
  private messaging = inject(Messaging);
  private envInjector = inject(EnvironmentInjector); // Inject EnvironmentInjector
  fcms = signal<string[]>([]);
  firebaseServiceWorkerPath = environment.production
    ? '../../assets/firebase-messaging-sw.prod.js'
    : '../../assets/firebase-messaging-sw.js';

  constructor() {
    this.store
      .select(selectUser)
      .pipe(
        map((user) => {
          // TODO : get array of already existing FCM and use it in the resgister method
          // console.log('use this to get the already existing fcm tokens', res);
          if (user) {
            // this.fcms.update(res.user.fcm) TODO : fcm doesn't exist in UserDetails
          }
        })
      )
      .subscribe();
  }

  registerTokenV3(userId: string) {
    Notification.requestPermission().then(
      (notificationPermissions: NotificationPermission) => {
        if (notificationPermissions === 'granted') {
          // console.log('Granted');
          // use service worker to register token
          navigator.serviceWorker
            .register(this.firebaseServiceWorkerPath, {
              type: 'module',
            })
            .then((serviceWorkerRegistration) => {
              runInInjectionContext(this.envInjector, () => {
                getToken(this.messaging, {
                  vapidKey: environment.firebaseConfig.vapidKey,
                  serviceWorkerRegistration,
                }).then((token) => {
                  if (token) {
                    this.apollo
                      .mutate({
                        mutation: UpdateFCM,
                        variables: { token },
                      })
                      .subscribe();
                  }
                });
              });
            });

          const message$ = new Observable((sub) =>
            onMessage(this.messaging, (msg) => sub.next(msg))
          ).pipe(map((msg) => {}));
          message$.subscribe({
            next: (msg) => {},
            error: (err) => console.error('FCM message error:', err?.message),
          });

          onMessage(this.messaging, {
            next: (payload) => {
              // You can display the message or do something else with it
            },
            error: (error) => console.error('FCM message error:', error?.message),
            complete: () => {},
          });
        }
        if (notificationPermissions === 'denied') {
          // console.log('Denied');
        }
      }
    );
    this.checkAppUpdate();

    // const backgroundMessage$ = new Observable((sub)=>
    //   onBackgroundMessage(this.messaging, (payload) => {
    //     console.log('[firebase-messaging-sw.js] Received background message ', payload);
    //     // Customize notification here
    //     const notificationTitle = 'Background Message Title';
    //     const notificationOptions = {
    //       body: 'Background Message body.',
    //       icon: '/firebase-logo.png'
    //     };

    //     self.registration.showNotification(notificationTitle,
    //       notificationOptions);
    //   });
    // )
  }

  removeFCM(): Observable<any> {
    return from(
      runInInjectionContext(this.envInjector, () => {
        return getToken(this.messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
        });
      })
    ).pipe(
      switchMap((fcm) => {
        // console.log(
        //   'fetched token again with get token to remove from db',
        //   fcm
        // );
        return this.apollo.mutate({
          mutation: RemoveFCM,
          variables: {
            token: fcm,
          },
        });
      })
    );
  }

  checkAppUpdate(): void {
    const isEnabled = this.swUpdate.isEnabled;
    if (!isEnabled) return;
    this.swUpdate.checkForUpdate().then((res) => {
      if (res) {
        logEvent(this.analytics, LogEventsEnum.app_update);
        window.location.reload();
      }
    });
  }
}
